import { getCatalogReference } from '@/mocks/gobeletcupCatalog';
import type { ICatalogReference, TQuadriPlacement } from '@/mocks/gobeletcupCatalog';
import type { TPrintTechnique } from '@/mocks/gobeletcupPricing';
import type { Product } from '@/domain/product/Product';

// The short quadri band on the cups that let the customer place it high, centred or low
// (ECO400, ECO450, ECO500, ECO900 and ECOTUBO25 in the technical sheets): a strip the width of the
// wall, about 70 mm tall. The backend serves the whole wall; when that style is in force the product
// is rewritten to the band's size, as the pitchers are to their face zone (withFaceZone), so the
// canvas is the band and the 3D view lays it on the wall where the customer put it, moving it
// as they tap (TheProduct3DView, ZonePlacementChoice). The help grid goes with it: it is drawn
// for the whole wall. Only the cups with a choice are rewritten, the others keep the socle's
// wall until the developers model the zones for real.
// Prototype data, CDC domain CFG. Tracked in PROTO-NOTES.md.

type TBandLayout = {
    /** Distance from the left edge of the wall to the left edge of the band. */
    leftInMm: number;
    surface: ICatalogReference['surface'];
    /** Distance from the top of the wall to the top of the band, for the placement in force. */
    topInMm: number;
    zone: { height: number; width: number };
};

function placeableBand(reference: ICatalogReference | undefined) {
    const band = reference?.quadriShort;
    if (!reference || !band || reference.hasVerso || Object.keys(band.placements).length < 2) {
        return undefined;
    }
    return band;
}

function withBandZone(product: Product, technique: TPrintTechnique): Product {
    const band = placeableBand(getCatalogReference(product.name));
    if (technique !== 'quadriShort' || !band) {
        return product;
    }
    return product.with({ height: band.height, helpGridRecto: null, helpGridVerso: null, width: band.width });
}

// Where the band's top stands on the wall for a position. The margins are the data, and the client's
// first ECO900 figures do not fit its wall (144 + 70 + 144 mm on 140): the band is kept on the cup
// whatever the margins say, the centre being the middle of the wall.
function bandTopInMm(reference: ICatalogReference, placement: TQuadriPlacement): number {
    const band = reference.quadriShort;
    if (!band) {
        return 0;
    }
    const lowestTopInMm = Math.max(reference.surface.height - band.height, 0);
    if (placement === 'centre') {
        const marginTopInMm = band.placements.centre?.marginTop ?? band.marginTop;
        const isOnTheWall = marginTopInMm >= 0 && marginTopInMm <= lowestTopInMm;
        return isOnTheWall ? marginTopInMm : lowestTopInMm / 2;
    }
    const marginTopInMm = band.placements[placement]?.marginTop ?? band.marginTop;
    return Math.min(Math.max(marginTopInMm, 0), lowestTopInMm);
}

function bandLayout(reference: ICatalogReference | undefined, placement: TQuadriPlacement): TBandLayout | undefined {
    const band = placeableBand(reference);
    if (!reference || !band) {
        return undefined;
    }
    return {
        leftInMm: (reference.surface.width - band.width) / 2,
        surface: reference.surface,
        topInMm: bandTopInMm(reference, placement),
        zone: { height: band.height, width: band.width },
    };
}

export { bandLayout, bandTopInMm, placeableBand, withBandZone };
export type { TBandLayout };
